<template>
  <div>
    <v-card
      shaped
      color="#CFD8DC"
    >
      <br>
      <v-card-title>
        <v-row>
          <v-col
            cols="12"
            lg="4"
          >
            <v-autocomplete
              v-model="selectedOrganizationId"
              menu-props="auto"
              :items="organizationList"
              item-value="external_id"
              item-text="name"
              label="Select Organization"
              outlined
              dense
              clearable
              @input="handleFilterChange()"
            />
          </v-col>
          <v-col
            cols="12"
            lg="3"
          >
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              item-value="value"
              item-text="text"
              label="Status"
              outlined
              dense
              clearable
              @input="handleFilterChange()"
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="purchaseRequests"
        class="elevation-1"
        disable-sort
        mobile-breakpoint="100"
        :items-per-page="itemsPerPage"
        :footer-props="{
          'items-per-page-options': [10, 20, 30],
        }"
        :server-items-length="totalRecords"
        :options.sync="options"
      >
        <template #item="{ item }">
          <tr>
            <td>{{ item.organization && item.organization.name }}</td>
            <td>
              {{ item.requested_by && item.requested_by.name }}
              <div
                v-if="item.requested_by"
                class="sub-text-style"
              >
                {{ item.requested_by.email }}
              </div>
            </td>
            <td class="package-style">
              {{ item.package_key }}
            </td>
            <td>{{ item.credits }}</td>
            <td>{{ item.price_aud }}</td>
            <td>
              <v-chip
                small
                outlined
                dark
                :color="statusColor(item.status)"
              >
                {{ item.status }}
              </v-chip>
            </td>
            <td>{{ item.note || '-' }}</td>
            <td>{{ item.reviewed_by || '-' }}</td>
            <td>{{ item.reviewed_at ? convertToLocal(item.reviewed_at) : '-' }}</td>
            <td>{{ item.review_note || '-' }}</td>
            <td>{{ convertToLocal(item.created_at) }}</td>
            <td>
              <template v-if="item.status === 'pending'">
                <v-btn
                  class="mt-n2"
                  elevation="1"
                  fab
                  x-small
                  icon
                  color="#2E7D32"
                  @click="confirmReview(item, 'approved')"
                >
                  <v-icon>mdi-check</v-icon>
                </v-btn>
                <v-btn
                  class="mt-n2 ml-2"
                  elevation="1"
                  fab
                  x-small
                  icon
                  color="#C62828"
                  @click="confirmReview(item, 'rejected')"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <template v-else>
                -
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>
      <div class="py-3" />
    </v-card>
    <centre-spinner
      :loading="loading"
    />
    <delete-dialog
      v-if="showReviewDialog"
      :title="reviewDialogTitle"
      :message="reviewDialogMessage"
      :button1="'No'"
      :button2="'Yes'"
      @closed="showReviewDialog = false"
      @confirmed="reviewConfirmed()"
    />
  </div>
</template>
<script>
  import moment from 'moment';
  import spinner from 'src/views/dashboard/component/SpinnerCentre';
  import DeleteDialog from 'src/views/dashboard/component/DeleteDialog';
  import Constants from 'src/constants';

  export default {
    name: 'AiCreditPurchaseRequests',
    components: {
      'centre-spinner': spinner,
      'delete-dialog': DeleteDialog,
    },
    data () {
      return {
        loading: false,
        selectedOrganizationId: '',
        selectedStatus: '',
        itemsPerPage: 20,
        options: {},
        showReviewDialog: false,
        reviewRequest: null,
        reviewStatus: '',
        statusOptions: [
          { text: 'Pending', value: 'pending' },
          { text: 'Approved', value: 'approved' },
          { text: 'Rejected', value: 'rejected' },
        ],
        headers: [
          { text: 'Organization', align: 'start', value: 'organization' },
          { text: 'Requested By', value: 'requested_by' },
          { text: 'Package', value: 'package_key' },
          { text: 'Credits', value: 'credits' },
          { text: 'Price (AUD)', value: 'price_aud' },
          { text: 'Status', value: 'status' },
          { text: 'Note', value: 'note' },
          { text: 'Reviewed By', value: 'reviewed_by' },
          { text: 'Reviewed At', value: 'reviewed_at' },
          { text: 'Review Note', value: 'review_note' },
          { text: 'Created At', value: 'created_at' },
          { text: 'Action', value: 'action' },
        ],
      };
    },
    computed: {
      aiCreditPurchaseRequestsList () {
        return this.$store.getters['payments/getAiCreditPurchaseRequestsList'];
      },
      purchaseRequests () {
        return this.aiCreditPurchaseRequestsList.data || [];
      },
      totalRecords () {
        return this.aiCreditPurchaseRequestsList.total || 0;
      },
      organizationList () {
        return this.$store.getters['organizations/getOrganizationList'];
      },
      reviewDialogTitle () {
        return this.reviewStatus === 'approved' ? 'Approve Request' : 'Reject Request';
      },
      reviewDialogMessage () {
        const action = this.reviewStatus === 'approved' ? 'approve' : 'reject';
        return `Are you sure you want to ${action} this purchase request?`;
      },
    },
    watch: {
      options: {
        async handler () {
          await this.getAiCreditPurchaseRequestsList();
        },
        deep: true,
      },
    },
    async mounted () {
      await this.getOrganizationsList();
    },
    methods: {
      convertToLocal (stringDatetime) {
        return moment(stringDatetime).local().format('Do MMMM YYYY hh:mm A');
      },
      statusColor (status) {
        if (status === 'approved') {
          return '#2E7D32';
        } else if (status === 'rejected') {
          return '#C62828';
        }
        return '#F57F17';
      },
      async getOrganizationsList () {
        this.loading = true;
        await this.$store.dispatch('organizations/fetchOrganizationList');
        this.loading = false;
      },
      async handleFilterChange () {
        this.options.page = 1;
        await this.getAiCreditPurchaseRequestsList();
      },
      async getAiCreditPurchaseRequestsList () {
        this.loading = true;
        await this.$store.dispatch('payments/fetchAiCreditPurchaseRequestsList', {
          params: {
            page: this.options.page,
            per_page: this.options.itemsPerPage,
            organization_id: this.selectedOrganizationId || null,
            status: this.selectedStatus || null,
          },
        });
        this.loading = false;
      },
      confirmReview (item, status) {
        this.reviewRequest = item;
        this.reviewStatus = status;
        this.showReviewDialog = true;
      },
      async reviewConfirmed () {
        this.showReviewDialog = false;
        this.loading = true;
        try {
          await this.$store.dispatch('payments/reviewAiCreditPurchaseRequest', {
            requestId: this.reviewRequest.id,
            status: this.reviewStatus,
          });
          this.$store.dispatch('alert/onAlert', {
            message: `Purchase request ${this.reviewStatus} successfully.`,
            type: Constants.ALERT_TYPE_SUCCESS,
          });
          await this.getAiCreditPurchaseRequestsList();
        } finally {
          this.loading = false;
        }
      },
    },
  };
</script>
<style scoped>
.sub-text-style {
  font-size: 11px;
  color: #607D8B;
}
.package-style {
  text-transform: capitalize;
}
.v-data-table::v-deep th {
  font-size: 12px !important;
  color: #37474F !important;
  font-weight: bold !important;
}
.v-data-table::v-deep td {
  font-size: 13px !important;
  color: #37474F !important;
}
.theme--light.v-data-table tbody tr:nth-of-type(even) {
  background-color: #CFD8DC
}
.v-data-table { background-color: #ECEFF1; }
</style>
